import React, { useImperativeHandle, useRef, useMemo } from 'react';
import 'swiper/css/pagination';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';

import SwiperSlideUnstyled from '@ecp/common/src/components/unstyled/swiper/SwiperSlideUnstyled';
import useSwipe from '@ecp/common/src/components/unstyled/swiper/useSwipe';
import SwiperItem from '@ecp/common/src/components/swiper/component/SwiperItem';

export const Wrapper = styled.div`
  .progressSwiper .swiper-pagination-progressbar {
    width: 1470px;
    height: 2px;
    top: 520px;
    left: 490px;
  }

  .progressSwiper .swiper-pagination-progressbar .swiper-pagination-progressbar-fill {
    background: #3c3c3c;
  }
`;

const SwiperHeroBannerUnstyled = React.forwardRef(({ onClick, ...props }, ref) => {
  const {
    width,
    height,
    round,
    backgroundColor,
    itemList,
    itemProps,
    itemTitleProps,
    pagingProps,
    rootComponent: RootComponent,
    wrapperComponent: WrapperComponent,
    controllerComponent: ControllerComponent,
    pagingComponent: PagingComponent,
    itemComponent: Item,
    rootProps,
    carouselProps,
    controlProps,
    initialSlide,
  } = props;

  const sliderRef = useRef(null);
  useImperativeHandle(ref, () => sliderRef.current);
  const {
    selectedIndex,
    showControl,
    totalSlideCount,
    handleItemClick,
    getControlProps,
    getCarouselProps,
    getSlideProps,
    getPagingProps,
  } = useSwipe(props, sliderRef, onClick);

  const processedList = useMemo(() => {
    if (itemList.length <= 1) {
      return itemList;
    }
    return [itemList.slice(-1)[0], ...itemList.slice(0, -1)];
  }, [itemList]);

  carouselProps.modules = { Pagination };
  return (
    <Wrapper>
      <RootComponent {...rootProps}>
        <WrapperComponent
          width={width}
          height={height}
          round={round}
          backgroundColor={backgroundColor}
          data-testid="carousel-wrapper"
          {...props}
        >
          {!!processedList.length && (
            <Swiper ref={sliderRef} {...getCarouselProps(carouselProps)} className="progressSwiper">
              {processedList.map((item, index) => (
                <SwiperSlide key={`swiper-slide-item__${index}`} {...getSlideProps(itemProps)} aria-label={''}>
                  <SwiperSlideUnstyled
                    index={index}
                    item={item}
                    current={initialSlide !== undefined ? initialSlide : selectedIndex}
                    total={totalSlideCount}
                    onClick={handleItemClick}
                  >
                    <Item
                      item={item}
                      itemProps={itemProps}
                      itemTitleProps={itemTitleProps}
                      index={index}
                      total={totalSlideCount}
                    />
                  </SwiperSlideUnstyled>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          {showControl && (
            <ControllerComponent
              {...getControlProps(controlProps)}
              data-testid="carousel-controller"
              itemList={processedList}
              handleItemClick={handleItemClick}
              itemComponent={Item}
            />
          )}
          {PagingComponent && <PagingComponent {...getPagingProps(pagingProps)} data-testid="carousel-paging" />}
        </WrapperComponent>
      </RootComponent>
    </Wrapper>
  );
});

SwiperHeroBannerUnstyled.propTypes = {
  itemList: PropTypes.array,
  itemProps: PropTypes.object,
  itemTitleProps: PropTypes.object,
  pagingProps: PropTypes.object,
  onClick: PropTypes.func,
  rootComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  wrapperComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  controllerComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  pagingComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  itemComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  carouselProps: PropTypes.object,
  width: PropTypes.string,
  height: PropTypes.string,
  round: PropTypes.bool,
  backgroundColor: PropTypes.string,
  initialSlide: PropTypes.number,
  rootProps: PropTypes.object,
  controlProps: PropTypes.object,
};

SwiperHeroBannerUnstyled.defaultProps = {
  itemList: [],
  carouselProps: {},
  controlProps: {},
  rootComponent: 'div',
  wrapperComponent: 'div',
  itemComponent: SwiperItem,
  onClick: () => {},
  itemTitleProps: {},
};

SwiperHeroBannerUnstyled.displayName = 'SwiperHeroBannerUnstyled';

export default SwiperHeroBannerUnstyled;
